import { useState, useEffect } from 'react';
import { useNotify, Title } from 'react-admin';
import {
  Box,
  Typography,
  Card,
  CardContent,
  InputBase,
  Avatar,
  Chip,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';

const API_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:3000';

interface Service {
  code: string;
  name: string;
  logo?: string | null;
}

const defaultColors = [
  { bg: 'rgba(115, 103, 240, 0.12)', color: '#7367f0' },
  { bg: 'rgba(0, 207, 232, 0.12)', color: '#00cfe8' },
  { bg: 'rgba(40, 199, 111, 0.12)', color: '#28c76f' },
  { bg: 'rgba(255, 159, 67, 0.12)', color: '#ff9f43' },
  { bg: 'rgba(234, 84, 85, 0.12)', color: '#ea5455' },
  { bg: 'rgba(206, 159, 252, 0.12)', color: '#ce9ffc' },
];

const getDefaultColor = (code: string) => {
  const index = code?.charCodeAt(0) % defaultColors.length || 0;
  return defaultColors[index];
};

export const ServiceList = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [filteredServices, setFilteredServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const notify = useNotify();

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/admin/sms/services`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const result = await response.json();
        if (result.data && Array.isArray(result.data)) {
          setServices(result.data);
          setFilteredServices(result.data);
        }
      } catch (error) {
        notify('Servisler yüklenemedi', { type: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [notify]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredServices(services);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = services.filter(
        (service) =>
          service.code?.toLowerCase().includes(term) ||
          service.name?.toLowerCase().includes(term)
      );
      setFilteredServices(filtered);
    }
  }, [searchTerm, services]);

  const renderServiceIcon = (service: Service) => {
    if (service.logo) {
      return (
        <Avatar
          src={`${API_URL}${service.logo}`}
          sx={{ width: 48, height: 48, backgroundColor: '#161d31' }}
          variant="rounded"
        >
          {service.code?.substring(0, 2).toUpperCase()}
        </Avatar>
      );
    }

    const defaultColor = getDefaultColor(service.code);
    return (
      <Avatar
        sx={{
          width: 48,
          height: 48,
          backgroundColor: defaultColor.bg,
          color: defaultColor.color,
          fontSize: '1rem',
          fontWeight: 700,
        }}
      >
        {service.code?.substring(0, 2).toUpperCase()}
      </Avatar>
    );
  };

  return (
    <Box>
      <Title title="Servisler" />

      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#d0d2d6' }}>
            Servisler
          </Typography>
          <Typography variant="body2" sx={{ color: '#676d7d', mt: 0.5 }}>
            {filteredServices.length} servis bulundu
          </Typography>
        </Box>

        {/* Search Box */}
        <Card sx={{ backgroundColor: '#283046' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1,
              minWidth: 300,
            }}
          >
            <SearchIcon sx={{ color: '#676d7d', fontSize: 20, mr: 1.5 }} />
            <InputBase
              placeholder="Servis adı veya kod ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                color: '#d0d2d6',
                fontSize: '0.875rem',
                flex: 1,
                '& ::placeholder': {
                  color: '#676d7d',
                  opacity: 1,
                },
              }}
            />
            {searchTerm && (
              <Chip
                label={`${filteredServices.length} sonuç`}
                size="small"
                sx={{
                  backgroundColor: 'rgba(115, 103, 240, 0.12)',
                  color: '#7367f0',
                  fontSize: '0.7rem',
                  height: 22,
                }}
              />
            )}
          </Box>
        </Card>
      </Box>

      {/* Stats Card */}
      <Card
        sx={{
          background: 'linear-gradient(118deg, #ff9f43, rgba(255, 159, 67, 0.7))',
          mb: 3,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Toplam Servis
              </Typography>
              <Typography variant="h3" sx={{ color: 'white', fontWeight: 700 }}>
                {services.length}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
                Aktif SMS servisleri
              </Typography>
            </Box>
            <Avatar
              sx={{
                width: 70,
                height: 70,
                backgroundColor: 'rgba(255,255,255,0.2)',
              }}
            >
              <PhoneAndroidIcon sx={{ fontSize: 36, color: 'white' }} />
            </Avatar>
          </Box>
        </CardContent>
        <Box
          sx={{
            position: 'absolute',
            right: -40,
            bottom: -40,
            width: 150,
            height: 150,
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.1)',
          }}
        />
      </Card>

      {/* Loading */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#7367f0' }} />
        </Box>
      ) : (
        /* Services Grid */
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 2,
          }}
        >
          {filteredServices.map((service) => (
            <Card
              key={service.code}
              sx={{
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                },
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {renderServiceIcon(service)}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: '#d0d2d6',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {service.name}
                    </Typography>
                    <Chip
                      label={service.code}
                      size="small"
                      sx={{
                        mt: 0.5,
                        height: 22,
                        fontSize: '0.7rem',
                        backgroundColor: 'rgba(115, 103, 240, 0.12)',
                        color: '#7367f0',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* No Results */}
      {!loading && filteredServices.length === 0 && (
        <Card>
          <CardContent sx={{ py: 8, textAlign: 'center' }}>
            <SearchIcon sx={{ fontSize: 48, color: '#676d7d', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#d0d2d6' }}>
              Sonuç bulunamadı
            </Typography>
            <Typography variant="body2" sx={{ color: '#676d7d', mt: 1 }}>
              "{searchTerm}" ile eşleşen servis bulunamadı
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
